import type { LineupMember } from "@/lib/v2/lineup";

/** Stable pseudo-random order for tie-breaking when balance scores are equal. */
function seededOrder(keys: string[], seed: string): string[] {
  let state = 0;
  for (let i = 0; i < seed.length; i++) {
    state = (state * 31 + seed.charCodeAt(i)) | 0;
  }
  const arr = [...keys];
  for (let i = arr.length - 1; i > 0; i--) {
    state = (state * 1103515245 + 12345) | 0;
    const j = Math.abs(state) % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function assignMember(
  member: LineupMember,
  teamA: LineupMember[],
  teamB: LineupMember[]
): "A" | "B" {
  const sumA = teamA.reduce((s, p) => s + p.balanceScore, 0);
  const sumB = teamB.reduce((s, p) => s + p.balanceScore, 0);
  const costA = Math.abs(sumA + member.balanceScore - sumB);
  const costB = Math.abs(sumB + member.balanceScore - sumA);
  if (costA < costB) return "A";
  if (costB < costA) return "B";
  return teamA.length <= teamB.length ? "A" : "B";
}

/** Split participants into two balanced teams (preview or publish). */
export function buildBalancedLineup(
  participants: LineupMember[],
  seed: string
): { teamA: string[]; teamB: string[] } {
  if (participants.length === 0) return { teamA: [], teamB: [] };

  const byKey = new Map(participants.map((p) => [p.key, p]));
  const orderedKeys = seededOrder(
    participants.map((p) => p.key),
    seed
  );

  const sorted = [...orderedKeys]
    .map((key) => byKey.get(key)!)
    .sort((a, b) => {
      const diff = b.balanceScore - a.balanceScore;
      if (diff !== 0) return diff;
      return orderedKeys.indexOf(a.key) - orderedKeys.indexOf(b.key);
    });

  const teamA: LineupMember[] = [];
  const teamB: LineupMember[] = [];

  for (const member of sorted) {
    const side = assignMember(member, teamA, teamB);
    if (side === "A") teamA.push(member);
    else teamB.push(member);
  }

  return {
    teamA: teamA.map((m) => m.key),
    teamB: teamB.map((m) => m.key),
  };
}

/** @deprecated Use buildBalancedLineup with LineupMember[] — kept for numeric-only callers. */
export function buildTentativeLineup(
  playerIds: number[],
  seed: string
): { teamA: number[]; teamB: number[] } {
  const participants: LineupMember[] = playerIds.map((id) => ({
    key: `player-${id}`,
    name: String(id),
    playerId: id,
    balanceScore: 75,
  }));
  const { teamA, teamB } = buildBalancedLineup(participants, seed);
  return {
    teamA: teamA.map((k) => Number(k.replace(/^player-/, ""))),
    teamB: teamB.map((k) => Number(k.replace(/^player-/, ""))),
  };
}

export function keysToMembers(
  keys: string[],
  participantsByKey: Map<string, LineupMember>
): LineupMember[] {
  return keys
    .map((key) => participantsByKey.get(key))
    .filter((m): m is LineupMember => m != null);
}
